import { useCallback, useMemo, useState } from "react";

export const API_SORT_FIELDS = new Set([
	"pos",
	"actionName",
	"name",
	"assets",
	"operatingModeName",
	"systemName",
	"roles",
	"estimatedMinutes",
	"intervals",
	"stages",
	"zones",
	"arrangements",
]);

// Sole owner of the taskSort storage format: a localStorage-only [id,
// direction] pair. Read and write both live here so they can't drift apart.
const TASK_SORT_STORAGE_KEY = "taskSort";

const readStoredSort = () => {
	try {
		return JSON.parse(localStorage.getItem(TASK_SORT_STORAGE_KEY));
	} catch {
		return null;
	}
};

const writeStoredSort = (id, direction) => {
	localStorage.setItem(TASK_SORT_STORAGE_KEY, JSON.stringify([id, direction]));
};

const getInitialSorting = () => {
	const saved = readStoredSort();
	const [id = "pos", direction = "desc"] = saved || [];
	return [{ id: API_SORT_FIELDS.has(id) ? id : "pos", desc: direction === "desc" }];
};

// onSortChange lets the caller drop an in-progress "jump to this task" anchor
// whenever the sort changes, since a new order makes that anchor meaningless.
const useTaskSort = ({ onSortChange } = {}) => {
	const [sorting, setSortingState] = useState(getInitialSorting);
	const activeSort = useMemo(() => sorting[0] ?? { id: "pos", desc: false }, [sorting]);

	const setSorting = useCallback(
		(updater) => {
			onSortChange?.();
			setSortingState((current) => {
				const next = typeof updater === "function" ? updater(current) : updater;
				const selected = next[0];
				if (selected) writeStoredSort(selected.id, selected.desc ? "desc" : "asc");
				return next;
			});
		},
		[onSortChange]
	);

	return { sorting, activeSort, setSorting };
};

export default useTaskSort;
